import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    errorRedirect,
    FormStore,
    inject,
    Input,
    Loader,
    Modal,
    ModalHandle,
    Nullable,
    SelectInput,
    SubTopBar,
    TasksStore,
    toArray,
    upperCaseEachWord,
    uuidv4generate,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowLeftShort } from '@react-icons/all-files/bs/BsArrowLeftShort';
import { LinkContainer } from 'react-router-bootstrap';
import { ValueType } from 'react-select/src/types';
import { Button, Form, FormGroup } from 'reactstrap';
import { PartDefinitionsStore } from '../PartDefinitions/PartDefinitionsStore';
import { ModelDefinitionsStore } from './ModelDefinitionsStore';

interface ISelectOption {
    value: string;
    label: string;
}

export interface ICreateOrEditModelDefinitionProps {
    readonly guid?: string;
    readonly modalHandle: ModalHandle;
    readonly viewState: 'create' | 'edit';
}

@autoinject
@observer
export class CreateOrEditModelDefinition extends React.Component<ICreateOrEditModelDefinitionProps> {
    public render() {
        return (
            <Modal
                title={(this.props.viewState == 'edit' ? 'Edit' : 'Create') + ' Model Definition'}
                modalHandle={this.props.modalHandle}
                size="lg"
                className="my-2"
                footerClass="d-none"
                onOpened={this.onOpenedAsync}
            >
                <div className="container-fluid pt-3">
                    <SubTopBar>
                        <LinkContainer exact to="/model-definitions/models">
                            <a className="btn btn-link">
                                <BsArrowLeftShort style={{ fontSize: 25 }} /> Back To Models
                            </a>
                        </LinkContainer>
                    </SubTopBar>

                    <Loader
                        loading={this.loading && !this.modelDefinitionForm}
                        message=" Loading Model..."
                        showSpinner
                        className="w-100 text-center"
                    />

                    {this.modelDefinitionForm && (
                        <Form onSubmit={this.submitAsync}>
                            {!!this.modelDefinitionForm.globalErrors.length && (
                                <div className="alert-danger p-2">
                                    <ul>
                                        {this.modelDefinitionForm.globalErrors.map((e: string, idx: number) => (
                                            <li key={idx}>{e}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div>
                                <FormGroup color={this.modelDefinitionForm.fields.modelNumber.state}>
                                    <label>
                                        <strong>Model Number</strong>
                                    </label>

                                    <Input
                                        type="text"
                                        field={this.modelDefinitionForm.fields.modelNumber}
                                        placeholder="Number"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.modelDefinitionForm.fields.description.state}>
                                    <label>
                                        <strong>Description</strong>
                                    </label>

                                    <Input
                                        type="text"
                                        field={this.modelDefinitionForm.fields.description}
                                        placeholder="Description"
                                    />
                                </FormGroup>

                                <FormGroup color={this.modelDefinitionForm.fields.partNumber.state}>
                                    <label>
                                        <strong>Top Level Part Number</strong>
                                    </label>

                                    <SelectInput
                                        field={this.modelDefinitionForm.fields.partNumber}
                                        options={this.partNumberOptions}
                                        placeholder="Part Numbers"
                                        required
                                    />
                                </FormGroup>

                                <FormGroup color={this.modelDefinitionForm.fields.isUnit.state}>
                                    <label>
                                        <strong>Is This A Unit?</strong>
                                    </label>

                                    <Input
                                        type="checkbox"
                                        field={this.modelDefinitionForm.fields.isUnit}
                                        placeholder="Is Unit"
                                        className="ms-5"
                                    />
                                </FormGroup>

                                <FormGroup>
                                    <Button type="submit" color="primary">
                                        <Loader loading={this.loading} showSpinner={true} message="Submitting..." />
                                        {!this.loading && (
                                            <span>
                                                {this.props.viewState == 'create' && 'Create Model Definition'}
                                                {this.props.viewState == 'edit' && 'Update Model Definition'}
                                            </span>
                                        )}
                                    </Button>
                                </FormGroup>
                            </div>
                        </Form>
                    )}
                </div>
            </Modal>
        );
    }

    @bind
    @action
    private async onOpenedAsync() {
        this.loading = true;
        try {
            this.fillFormAsync();
        } catch (e: any) {
            errorRedirect(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private onSelectPartNumber(newValue: ValueType<ISelectOption, boolean>) {
        if (!Array.isArray(newValue)) {
            const option = newValue as ISelectOption | null | undefined;
            if (option) this.modelDefinitionForm?.fields.partNumber.setValue(option.value);
        }
    }

    @computed
    private get modelDefinition() {
        if (this.props.guid) {
            return this.modelDefinitionsStore.modelDefinitions.get(this.props.guid);
        }
    }

    @action
    private async fillFormAsync() {
        if (this.props.viewState == 'edit' && !this.modelDefinition) throw new Error('Project does not exist');

        await this.partDefinitionsStore.loadAllTopLevelAsync();

        if (this.modelDefinition) {
            if (this.props.viewState == 'create') {
                this.modelDefinitionForm = new FormStore<Mri.ModelDefinitions.ModelDefinition>({
                    ...this.modelDefinition,
                    modelNumber: '',
                    modelDefinitionGuid: uuidv4generate(),
                });
            } else {
                this.modelDefinitionForm = new FormStore<Mri.ModelDefinitions.ModelDefinition>({
                    ...this.modelDefinition,
                });
            }
        } else {
            const model: Nullable<Mri.ModelDefinitions.ModelDefinition> = {
                modelNumber: '',
                description: '',
                partNumber: this.modelDefinitionForm?.fields.partNumber.value || '',
                modelDefinitionGuid: uuidv4generate(),
                isUnit: true,
            };

            this.modelDefinitionForm = new FormStore<Mri.ModelDefinitions.ModelDefinition>(model);
        }
    }

    @bind
    @action
    private async submitAsync(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!this.modelDefinitionForm) throw new Error('Form does not exist');

        this.modelDefinitionForm.onSubmit();
        if (this.modelDefinitionForm.validate()) {
            this.loading = true;
            const task = this.tasksStore.startSessionTask(upperCaseEachWord(this.props.viewState) + ' Config');

            try {
                if (this.props.viewState == 'edit' && this.props.guid) {
                    await this.modelDefinitionsStore.updateAsync(this.props.guid, this.modelDefinitionForm.values);
                } else if (this.props.viewState == 'create') {
                    await this.modelDefinitionsStore.createAsync(this.modelDefinitionForm.values);
                }
                task.complete();
            } catch (e: any) {
                if (e.modelState) {
                    this.modelDefinitionForm.clearGlobalErrors();
                    this.modelDefinitionForm.addErrors(e.modelState);
                }

                task.error(e);
            } finally {
                this.loading = false;
            }
        } else {
            console.log('invalid');
            this.loading = false;
        }

        this.props.modalHandle.close();
    }

    @computed
    private get partNumberOptions(): ISelectOption[] {
        const parts = toArray(this.partDefinitionsStore.topLevelPartDefinitions).map(a => {
            return {
                value: a.partNumber,
                label: a.partNumber,
            };
        });
        return parts;
    }

    @observable private modelDefinitionForm?: FormStore<Mri.ModelDefinitions.ModelDefinition>;
    @observable private loading = false;
    @inject private readonly partDefinitionsStore!: PartDefinitionsStore;
    @inject private readonly modelDefinitionsStore!: ModelDefinitionsStore;
    @inject private tasksStore!: TasksStore;
}
